/**
 * ENG-3453 — "none" choices are mutually exclusive.
 *
 * A choice like "I Do Not Have Any Assets" / "None of the above" can never be
 * true alongside a real selection ("Checking Account"), so it behaves as
 * exclusive. How a choice is classified, in order:
 *
 *   1. choice.is_exclusive === true   -> force ON  (explicit backend override)
 *   2. choice.is_exclusive === false  -> force OFF (explicit override; beats wording)
 *   3. is_exclusive null / undefined  -> automatic, decided from the choice wording
 *
 * The wording rule is intentionally conservative: only short labels are
 * considered, and only English/Spanish "none" phrasings.
 */

// Real "none" choices are short labels; a long paragraph that happens to
// contain a phrase like "none of the …" is not one.
const MAX_LABEL_LENGTH = 90;

export const EXCLUSIVE_CHOICE_PATTERN = new RegExp(
  [
    // English
    'do not have any',
    "don't have any",
    'none of the',
    'none of these',
    'have no',
    'no assets',
    'no income',
    'no source',
    'not have any',
    'not receive',
    'not entitled',
    'currently have no',
    'zero-income',
    'does not have any',
    // Spanish
    'no tengo ning',
    'no tengo ingresos',
    'no tengo bienes',
    'no tengo activos',
    'no tengo fuente',
    'no recibo',
    'no percibo',
    'sin ingresos',
    'ninguno de (los|estos|estas)',
    'ninguna de (las|estas)',
  ].join('|'),
  'i'
);

/**
 * @param {{ name?: string, is_exclusive?: boolean | null } | string | null | undefined} choice
 * @returns {boolean} true when the choice should behave as exclusive.
 */
export const isExclusiveChoice = (choice) => {
  if (!choice) {
    return false;
  }

  if (typeof choice === 'object') {
    if (choice.is_exclusive === true) {
      return true;
    }
    if (choice.is_exclusive === false) {
      return false;
    }
  }

  const name = typeof choice === 'string' ? choice : choice.name || '';

  return name.length > 0 && name.length <= MAX_LABEL_LENGTH
    ? EXCLUSIVE_CHOICE_PATTERN.test(name)
    : false;
};
